import { useMemo, useState } from 'react';
import { useMutation, useQueries, useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { Bug, ChevronDown, ChevronRight, ExternalLink, Github } from 'lucide-react';
import { engagementsApi, findingsApi, githubApi } from '../lib/api-client';
import { getPlainEnglishTitle } from '../lib/plain-english';
import { PageHeader } from '../components/layout/PageHeader';
import { Card, CardContent } from '../components/ui/card';
import { SeverityBadge, Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { LoadingState, ErrorState, EmptyState, friendlyErrorMessage } from '../components/ui/states';
import { useToast } from '../components/ui/toast';
import type { FindingRecord, FindingStatus, Severity } from '../lib/types';

const STATUS_LABEL: Record<FindingStatus, string> = {
  open: 'Open',
  remediated: 'Fixed',
  regressed: 'Came back',
  false_positive: 'Not a real issue',
};

/** Lower rank sorts first — CRITICAL at the top of each target group. */
const SEVERITY_RANK: Record<Severity, number> = { critical: 0, high: 1, medium: 2, low: 3, info: 4 };
const SEVERITY_ORDER: Severity[] = ['critical', 'high', 'medium', 'low', 'info'];

function highestSeverity(findings: FindingRecord[]): Severity {
  return findings.reduce<Severity>((worst, f) => (SEVERITY_RANK[f.severity] < SEVERITY_RANK[worst] ? f.severity : worst), 'info');
}

export function Findings() {
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [severity, setSeverity] = useState<Severity | 'all'>('all');
  const [status, setStatus] = useState<FindingStatus | 'all'>('all');
  // Seeded from the URL so a link like /findings?target=host-1 (see EndpointTesting's/RunDetail's
  // "N findings →" links) arrives pre-filtered; changing the dropdown updates the target state
  // too (though, unlike engagementId below, the target dropdown doesn't write back to the URL —
  // matching this page's pre-existing behavior for the target filter).
  const [target, setTarget] = useState<string>(searchParams.get('target') ?? 'all');
  // Seeded from the URL so a link like /findings?engagementId=e1 (see RunDetail's
  // "N findings →" link) arrives pre-filtered; changing the dropdown updates the URL too, so the
  // filtered view stays shareable/bookmarkable.
  const [engagementId, setEngagementId] = useState<string>(searchParams.get('engagementId') ?? 'all');
  const [collapsedTargets, setCollapsedTargets] = useState<Set<string>>(new Set());
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [exportRepoKey, setExportRepoKey] = useState<string>('');

  // Same query the GitHub Enterprise settings page (GitHubEnterprise.tsx) uses — the "export to
  // GitHub" button below only ever appears once this tenant actually has a connected org with at
  // least one allowlisted repo, matching the fail-closed allowlist gate the export API itself enforces.
  const githubInstallationQuery = useQuery({ queryKey: ['github', 'installation'], queryFn: () => githubApi.installation() });
  const githubConnected = githubInstallationQuery.data?.installation?.status === 'active';
  const githubAllowlistQuery = useQuery({
    queryKey: ['github', 'repos', 'allowlist'],
    queryFn: () => githubApi.allowlist(),
    enabled: githubConnected,
  });
  const allowlistedRepos = githubAllowlistQuery.data?.repos ?? [];
  const selectedRepo = allowlistedRepos.find((r) => `${r.owner}/${r.repo}` === exportRepoKey) ?? allowlistedRepos[0];

  const bulkExportMutation = useMutation({
    mutationFn: (input: { findingIds: string[]; owner: string; repo: string }) => findingsApi.bulkExportToGithub(input),
    onSuccess: (result) => {
      const succeeded = result.results.filter((r) => r.ok).length;
      const failed = result.results.length - succeeded;
      toast({
        title: failed === 0 ? `Exported ${succeeded} finding${succeeded === 1 ? '' : 's'} to GitHub` : `Exported ${succeeded}, ${failed} failed`,
        description: failed > 0 ? 'Some findings could not be exported — see the audit log for details.' : undefined,
        variant: failed === 0 ? 'success' : 'error',
      });
      setSelectedIds(new Set());
    },
    onError: (err) => toast({ title: 'Export failed', description: friendlyErrorMessage(err), variant: 'error' }),
  });

  const toggleFindingSelection = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const engagementsQuery = useQuery({ queryKey: ['engagements'], queryFn: () => engagementsApi.list() });
  const engagements = engagementsQuery.data?.engagements ?? [];

  const findingQueries = useQueries({
    queries: engagements.map((e) => ({
      queryKey: ['findings', e.id],
      queryFn: () => findingsApi.forEngagement(e.id),
      enabled: engagements.length > 0,
    })),
  });

  // Tenant-wide, source-agnostic fetch (BUI-1016) — the only path by which agent-sourced findings
  // (internal-agent BUI-1015 / endpoint-agent BUI-1016, both engagementId: null) ever reach this
  // page; the per-engagement queries above can never see them. Merged below by `id` — in practice
  // there's no real overlap (run-sourced findings always have an engagementId, agent-sourced ones
  // never do), but a `Map` keyed by `id` dedupes defensively anyway.
  const tenantFindingsQuery = useQuery({ queryKey: ['findings', 'tenant-wide'], queryFn: () => findingsApi.listForTenant() });

  const engagementNameById = useMemo(
    () => new Map((engagementsQuery.data?.engagements ?? []).map((e) => [e.id, e.name])),
    [engagementsQuery.data],
  );

  const allFindings: FindingRecord[] = useMemo(() => {
    const byId = new Map<string, FindingRecord>();
    for (const q of findingQueries) {
      for (const f of Object.values(q.data?.byZone ?? {}).flat()) byId.set(f.id, f);
    }
    for (const f of tenantFindingsQuery.data?.findings ?? []) byId.set(f.id, f);
    return [...byId.values()];
  }, [findingQueries, tenantFindingsQuery.data]);

  const targetOptions = useMemo(() => [...new Set(allFindings.map((f) => f.target))].sort(), [allFindings]);

  const filtered = allFindings.filter(
    (f) =>
      (severity === 'all' || f.severity === severity) &&
      (status === 'all' || f.status === status) &&
      (target === 'all' || f.target === target) &&
      (engagementId === 'all' || f.engagementId === engagementId),
  );

  const severityCounts = useMemo(() => {
    const counts: Record<Severity, number> = { critical: 0, high: 0, medium: 0, low: 0, info: 0 };
    for (const f of filtered) counts[f.severity]++;
    return counts;
  }, [filtered]);

  // Grouped by target, each group's findings sorted worst-severity-first, groups themselves
  // ordered by their own highest severity so the most urgent targets surface at the top.
  const groups = useMemo(() => {
    const byTarget = new Map<string, FindingRecord[]>();
    for (const f of filtered) {
      const list = byTarget.get(f.target) ?? [];
      list.push(f);
      byTarget.set(f.target, list);
    }
    return [...byTarget.entries()]
      .map(([targetValue, findings]) => ({
        target: targetValue,
        findings: findings.slice().sort((a, b) => SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity]),
        highest: highestSeverity(findings),
      }))
      .sort((a, b) => SEVERITY_RANK[a.highest] - SEVERITY_RANK[b.highest]);
  }, [filtered]);

  const toggleTarget = (targetValue: string) => {
    setCollapsedTargets((prev) => {
      const next = new Set(prev);
      if (next.has(targetValue)) next.delete(targetValue);
      else next.add(targetValue);
      return next;
    });
  };

  const handleEngagementChange = (value: string) => {
    setEngagementId(value);
    const next = new URLSearchParams(searchParams);
    if (value === 'all') next.delete('engagementId');
    else next.set('engagementId', value);
    setSearchParams(next, { replace: true });
  };

  if (engagementsQuery.isLoading) return <LoadingState />;
  if (engagementsQuery.isError) return <ErrorState error={engagementsQuery.error} onRetry={() => engagementsQuery.refetch()} />;

  return (
    <div>
      <PageHeader title="Findings" description="Everything discovered across your engagements, in plain language." />

      <div className="mb-4 flex flex-wrap gap-2">
        {SEVERITY_ORDER.map((s) => (
          <Badge key={s} variant={s} className="capitalize">
            {severityCounts[s]} {s}
          </Badge>
        ))}
      </div>

      <div className="mb-4 flex flex-wrap gap-3">
        <Select value={severity} onValueChange={(v) => setSeverity(v as Severity | 'all')}>
          <SelectTrigger className="w-40" aria-label="Severity">
            <SelectValue placeholder="Severity" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All severities</SelectItem>
            {SEVERITY_ORDER.map((s) => (
              <SelectItem key={s} value={s} className="capitalize">
                {s}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={(v) => setStatus(v as FindingStatus | 'all')}>
          <SelectTrigger className="w-44" aria-label="Status">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {(Object.entries(STATUS_LABEL) as Array<[FindingStatus, string]>).map(([value, label]) => (
              <SelectItem key={value} value={value}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={target} onValueChange={setTarget}>
          <SelectTrigger className="w-48" aria-label="Target">
            <SelectValue placeholder="Target" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All targets</SelectItem>
            {targetOptions.map((t) => (
              <SelectItem key={t} value={t}>
                {t}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={engagementId} onValueChange={handleEngagementChange}>
          <SelectTrigger className="w-52" aria-label="Engagement">
            <SelectValue placeholder="Engagement" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All engagements</SelectItem>
            {engagements.map((e) => (
              <SelectItem key={e.id} value={e.id}>
                {e.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {selectedIds.size > 0 && githubConnected ? (
        <div className="mb-4 flex flex-wrap items-center gap-3 rounded-2xl border border-border bg-surface-sunken px-4 py-3">
          <span className="text-sm text-ink">
            {selectedIds.size} finding{selectedIds.size === 1 ? '' : 's'} selected
          </span>
          {allowlistedRepos.length === 0 ? (
            <span className="text-xs text-ink-muted">
              Connect a repository on the{' '}
              <Link to="/github" className="underline">
                GitHub Enterprise
              </Link>{' '}
              page before exporting.
            </span>
          ) : (
            <>
              <Select value={selectedRepo ? `${selectedRepo.owner}/${selectedRepo.repo}` : ''} onValueChange={setExportRepoKey}>
                <SelectTrigger className="w-56" aria-label="Repository">
                  <SelectValue placeholder="Repository" />
                </SelectTrigger>
                <SelectContent>
                  {allowlistedRepos.map((r) => (
                    <SelectItem key={`${r.owner}/${r.repo}`} value={`${r.owner}/${r.repo}`}>
                      {r.owner}/{r.repo}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                size="sm"
                disabled={!selectedRepo || bulkExportMutation.isPending}
                onClick={() =>
                  selectedRepo &&
                  bulkExportMutation.mutate({ findingIds: [...selectedIds], owner: selectedRepo.owner, repo: selectedRepo.repo })
                }
              >
                <Github size={14} /> Export {selectedIds.size} finding{selectedIds.size === 1 ? '' : 's'} to GitHub
              </Button>
            </>
          )}
          <Button variant="ghost" size="sm" onClick={() => setSelectedIds(new Set())}>
            Clear selection
          </Button>
        </div>
      ) : null}

      {groups.length === 0 ? (
        <EmptyState icon={<Bug size={32} />} title="No findings match" description="Nothing here yet, or try clearing your filters." />
      ) : (
        <div className="space-y-4">
          {groups.map((group) => {
            const isCollapsed = collapsedTargets.has(group.target);
            return (
              <div key={group.target} className="rounded-2xl border border-border">
                <button
                  type="button"
                  onClick={() => toggleTarget(group.target)}
                  aria-expanded={!isCollapsed}
                  className="flex w-full items-center justify-between gap-3 rounded-2xl bg-surface-sunken px-4 py-3 text-left"
                >
                  <div className="flex min-w-0 items-center gap-2">
                    {isCollapsed ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
                    <span className="truncate font-semibold text-ink">{group.target}</span>
                    <span className="shrink-0 text-xs text-ink-muted">
                      {group.findings.length} finding{group.findings.length === 1 ? '' : 's'}
                    </span>
                  </div>
                  <SeverityBadge severity={group.highest} />
                </button>

                {isCollapsed ? null : (
                  <div className="space-y-2 p-3">
                    {group.findings.map((finding) => (
                      <Card key={finding.id}>
                        <CardContent className="flex flex-wrap items-center justify-between gap-3 pt-6">
                          <div className="flex min-w-0 items-center gap-3">
                            <input
                              type="checkbox"
                              aria-label={`Select "${finding.title}" for export`}
                              className="size-4 shrink-0 rounded border-border accent-brand"
                              checked={selectedIds.has(finding.id)}
                              onChange={() => toggleFindingSelection(finding.id)}
                            />
                            <div className="min-w-0">
                              <p className="truncate font-medium text-ink">{getPlainEnglishTitle(finding.title) ?? finding.title}</p>
                              {getPlainEnglishTitle(finding.title) ? (
                                <p className="truncate text-xs text-ink-faint">{finding.title}</p>
                              ) : null}
                              <p className="truncate text-xs text-ink-muted">
                                {finding.engagementId
                                  ? (engagementNameById.get(finding.engagementId) ?? finding.engagementId)
                                  : 'Agent-reported'}{' '}
                                · {finding.target} · {finding.zone}
                              </p>
                            </div>
                          </div>
                          <div className="flex shrink-0 items-center gap-2">
                            <SeverityBadge severity={finding.severity} />
                            <Badge variant="neutral">{STATUS_LABEL[finding.status]}</Badge>
                            <Button size="sm" asChild>
                              <Link to={`/findings/${finding.id}`}>
                                <ExternalLink size={14} /> View details
                              </Link>
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
